import { AlertCircle, Clock, Loader2, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/auth/use-auth";
import { APIError, api } from "@/lib/api";
import { mergeOrgScopedItem, setOrgScopedItem } from "@/lib/saved-view-utils";
import { setShareUrlRestoring } from "@/lib/share-url-utils";

interface SharedStateData {
  state_data: {
    page: string;
    orgId: string;
    sessionStorage: Record<string, unknown>;
    localStorage: Record<string, unknown>;
    timestamp: string;
  };
  created_at: string;
  id: string;
  name: string | null;
  description: string;
  page_type: string | null;
  share_type?: "saved_view" | "one_time";
  is_one_time?: boolean;
  message?: string;
}

// Keys that should be merged (additive) rather than replaced
const MERGE_KEYS = [
  "campaigns::list::columns::formula-columns",
  "campaigns::explorer::benchmark::configs",
  "::columns::aliases", // Matches all entity aliases (campaigns, channels, salespeople, webpages, etc.)
];

/**
 * Check if a storage key should be merged with existing data rather than replaced
 */
const shouldMergeKey = (key: string): boolean => {
  return MERGE_KEYS.some((mergeKey) => key.includes(mergeKey));
};

/**
 * Merge shared value with existing storage value
 * Handles arrays by concatenating and deduplicating by id
 * Handles objects by merging properties
 */
// Local merge util removed; using mergeOrgScopedItem from saved-view-utils

export function ShareUrlHandler() {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectiveTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAndApplySharedState = async () => {
      if (!shortId) {
        setError("Invalid share URL");
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<SharedStateData>(`/s/${shortId}/`);

        // Ensure response exists
        if (!response) {
          setError("Invalid share URL data");
          setLoading(false);
          return;
        }

        const organizationId = user?.company?.default_organization?.id;

        if (!organizationId) {
          setError("No organization found - please log in");
          setLoading(false);
          return;
        }

        // Check if this is a saved view or one-time share
        const shareType =
          response.share_type || (response.name ? "saved_view" : "one_time");

        if (shareType === "saved_view" && response.name) {
          // It's a saved view - redirect to the page with ?view=id parameter
          let targetPage = "/";

          // Map page_type to actual route paths
          if (response.page_type) {
            // Handle page types with ::list suffix and without
            const pageTypeMap: Record<string, string> = {
              "campaigns::list": "/campaigns/list",
              "accounts::list": "/accounts/list",
              "opportunities::list": "/opportunities/list",
              "channels::list": "/channels/list",
              "salespeople::list": "/salespeople/list",
              "webpages::list": "/webpages/list",
              campaigns: "/campaigns/list",
              accounts: "/accounts/list",
              opportunities: "/opportunities/list",
              channels: "/channels/list",
              salespeople: "/salespeople/list",
              webpages: "/webpages/list",
              home: "/home",
              settings: "/settings",
              "deep-research": "/deep-research/datasets",
            };
            targetPage = pageTypeMap[response.page_type] || "/";
          }

          // Fall back to state_data.page if available
          if (response.state_data?.page) {
            targetPage = response.state_data.page;
          }

          const finalUrl = `${targetPage}?view=${shortId}`;
          navigate(finalUrl, { replace: true });

          toast.success(`Loading saved view: ${response.name}`);
          return;
        }

        // It's a one-time share - apply the state
        const state = response.state_data;

        // Validate state structure for one-time shares
        if (!state || typeof state !== "object") {
          setError("Invalid share URL data");
          setLoading(false);
          return;
        }

        // Helper function to determine if a key should be ignored during restoration
        const shouldIgnoreKey = (key: string): boolean => {
          // Patterns to ignore during restoration (user-specific transient state)
          const ignorePatterns = [
            "::recent", // Recent selections are user-specific
            "::current-user", // Current user selections
            "::ui-state", // Transient UI state
            "::focused", // Focus states
            "::scroll-position", // Scroll positions
            "::modal-open", // Modal states
            "::loading", // Loading states
            "::error-state", // Error states
            "saved-views::", // Don't override saved view state
          ];

          return ignorePatterns.some((pattern) => key.includes(pattern));
        };

        // Apply session storage data with selective restoration
        if (state?.sessionStorage) {
          Object.entries(state.sessionStorage).forEach(([key, value]) => {
            if (!shouldIgnoreKey(key)) {
              // Check if key already has organization prefix to avoid double-scoping
              setOrgScopedItem(organizationId, "sessionStorage", key, value);
            }
          });
        }

        // Apply local storage data with selective restoration and merging
        if (state?.localStorage) {
          Object.entries(state.localStorage).forEach(([key, value]) => {
            if (!shouldIgnoreKey(key)) {
              // Check if key already has organization prefix to avoid double-scoping
              if (shouldMergeKey(key)) {
                mergeOrgScopedItem(organizationId, "localStorage", key, value);
              } else {
                setOrgScopedItem(organizationId, "localStorage", key, value);
              }
            }
          });
        }

        // Set restoration flag before navigation to prevent race conditions
        setShareUrlRestoring(true);

        // Navigate to the original page and restore state immediately
        navigate(state?.page || "/", { replace: true });

        // Re-apply session storage data after navigation
        if (state?.sessionStorage) {
          Object.entries(state.sessionStorage).forEach(([key, value]) => {
            if (!shouldIgnoreKey(key)) {
              // Check if key already has organization prefix to avoid double-scoping
              setOrgScopedItem(organizationId, "sessionStorage", key, value);
            }
          });
        }

        // Re-apply local storage data after navigation with merging support
        if (state?.localStorage) {
          Object.entries(state.localStorage).forEach(([key, value]) => {
            if (!shouldIgnoreKey(key)) {
              // Check if key already has organization prefix to avoid double-scoping
              if (shouldMergeKey(key)) {
                mergeOrgScopedItem(organizationId, "localStorage", key, value);
              } else {
                setOrgScopedItem(organizationId, "localStorage", key, value);
              }
            }
          });
        }

        // Add a key to indicate this is a one-time share restore
        // This will trigger hasChanges in the saved view context
        setOrgScopedItem(
          organizationId,
          "sessionStorage",
          "saved-views::one-time-restore",
          {
            timestamp: Date.now(),
            shortId,
          },
        );

        // Clear the restoration flag immediately after restoration
        setShareUrlRestoring(false);

        // Show toast notification for one-time share
        toast.info("One-time share URL loaded");
      } catch (error) {
        // Clear the restoration flag on error as well
        setShareUrlRestoring(false);

        // Provide more specific error messages
        if (error instanceof APIError) {
          if (error.status === 404) {
            setError("Share URL not found");
            // Also show a toast for better visibility
            toast.error("Share URL not found", {
              description:
                "This share link may have been deleted or you don't have permission to access it.",
            });
          } else if (error.status === 401 || error.status === 403) {
            setError("Access denied. Please log in or check your permissions.");
            toast.error("Access denied", {
              description:
                "You don't have permission to access this shared view.",
            });
          } else {
            setError("Failed to load shared view. Please try again.");
            toast.error("Failed to load shared view", {
              description:
                "An error occurred while loading the shared view. Please try again.",
            });
          }
        } else if (error instanceof Error) {
          setError(`Error: ${error.message}`);
          toast.error("Failed to load shared view", {
            description: error.message,
          });
        } else {
          setError("Share URL not found or expired");
          toast.error("Share URL not found", {
            description: "This share link may be invalid or expired.",
          });
        }

        setLoading(false);
      }
    };

    loadAndApplySharedState();
  }, [shortId, navigate, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading shared view...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Determine error type and customize message
    let errorIcon = <AlertCircle className="h-16 w-16" />;
    let errorTitle = "Share URL Error";
    let errorMessage = error;
    let showRetry = false;

    if (error.includes("not found") || error.includes("access denied")) {
      const iconSrc =
        effectiveTheme === "dark"
          ? "/upside-icon-dark.svg"
          : "/upside-icon-light.svg";
      errorIcon = (
        <img src={iconSrc} alt="Upside" className="h-24 w-24 mx-auto" />
      );
      errorTitle = "Share URL Not Found";
      errorMessage =
        "This share link may be invalid, deleted, or you may not have permission to access it.";
    } else if (error.includes("expired")) {
      errorIcon = <Clock className="h-16 w-16" />;
      errorTitle = "Share URL Expired";
      errorMessage = "This share link has expired and is no longer available.";
    } else if (error.includes("Authentication")) {
      errorIcon = <Lock className="h-16 w-16" />;
      errorTitle = "Authentication Required";
      errorMessage = "Please log in to access this shared view.";
    } else if (error.includes("Invalid")) {
      errorIcon = <AlertCircle className="h-16 w-16" />;
      errorTitle = "Invalid Share URL";
      errorMessage = "This share link format is not valid.";
    } else {
      showRetry = true;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-4">
          <div className="bg-card rounded-lg corner-smooth-lg shadow-lg border p-8 text-center space-y-6">
            {/* Error Icon */}
            <div className="text-muted-foreground flex justify-center">
              {errorIcon}
            </div>

            {/* Error Content */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold font-display text-foreground">
                {errorTitle}
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                {errorMessage}
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={() => navigate("/", { replace: true })}
                className="w-full"
                size="lg"
              >
                Go to Dashboard
              </Button>

              {showRetry && (
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // This shouldn't render as we navigate away
}
